import { EMPTY, catchError, filter, map, mergeMap, of, switchMap, takeUntil, tap } from 'rxjs';
import { inject } from '@angular/core';
import { AppStore } from '@navstation-portal/reporting/data-access/app';
import { EngineMode, FormState, StateFieldType } from '@navstation-portal/reporting/data-model/vessel-report';
import { toastEvents } from '@navstation-portal/shared/data-access/toaster';
import { VesselSpecStore, vesselSpecEvents } from '@navstation-portal/vessels/data-access/store';
import { VesselSpec } from '@navstation-portal/vessels/data-model/vessel';
import { mapResponse } from '@ngrx/operators';
import { signalStoreFeature } from '@ngrx/signals';
import { Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events';
import { operationalEvents } from '../operational/operational-events';
import { OperationalStore } from '../operational/operational.store';
import { EdnFileType, REPORT_API_SERVICE } from '../report-dependencies';
import { reportEvents } from '../report-events';
import { getFieldValueFromFormState } from '../vm-builder-helpers';
import { powerCalculationEvents, powerEvents, powerFlowMeterEvents, shorePowerDocumentEvents } from './power-events';
import type { ShorePowerFormFieldId } from './power.store';

const TC_RPM_LEGACY_MODEL_CUTOFF = new Date('2019-06-01T00:00:00');
const TC_RPM_TRIGGER_FIELDS = ['tcRpm1', 'tcRpm2', 'rpm'];

interface PowerStoreState {
  formState: () => FormState;
  pendingEdnFiles: () => Partial<Record<EdnFileType, File>>;
}

const collectFlowMeterInstallationPaths = (vesselSpec: VesselSpec): string[] => {
  const installations = [vesselSpec.main_flow_meter_installation, vesselSpec.boiler_flow_meter_installation];

  return installations
    .flatMap((installation) => [
      ...Object.values(installation?.flow_meters ?? {}),
      ...Object.values(installation?.gas_flow_meters ?? {}),
    ])
    .flatMap((meter) => [meter.current_flow_path, meter.prev_flow_path, meter.diff_flow_path])
    .filter(Boolean);
};

const ednFieldId = (ednType: 'edn' | 'ednPos') => (ednType === 'edn' ? 'ednFiles' : 'proofOfSustainabilityEdnFiles');

export function withPowerEffects() {
  return signalStoreFeature(
    withEventHandlers((rawStore) => {
      const store = rawStore as unknown as PowerStoreState;
      const events = inject(Events);
      const reportService = inject(REPORT_API_SERVICE);
      const operationalStore = inject(OperationalStore);
      const appStore = inject(AppStore);
      const vesselSpecStore = inject(VesselSpecStore);
      const toastDispatcher = injectDispatch(toastEvents);
      const shorePowerDocumentDispatcher = injectDispatch(shorePowerDocumentEvents);
      const powerDispatcher = injectDispatch(powerEvents);

      const clearShorePowerFields = (fields: { fieldId: ShorePowerFormFieldId; value: StateFieldType }[]) =>
        fields.forEach(({ fieldId, value }) =>
          powerDispatcher.updateFormStateValue({ sectionId: 'shorePower', fieldId, value })
        );

      const syncShorePowerReceptionPortFromCurrentPortName = () => {
        const portName = getFieldValueFromFormState(operationalStore.formState(), 'portAndPosition', 'portName') as
          | string
          | undefined;
        if (!portName) return;

        powerDispatcher.updateFormStateValue({ sectionId: 'shorePower', fieldId: 'receptionPort', value: portName });
      };

      const getCurrentValue = (fieldId: string) =>
        getFieldValueFromFormState(store.formState(), 'mainEngine', fieldId) as number | undefined;

      const getEngineModes = () =>
        getFieldValueFromFormState(store.formState(), 'mainEngine', 'engineModes') as EngineMode[] | undefined;

      const isLegacyReport = () => {
        const reportTo = getFieldValueFromFormState(operationalStore.formState(), 'reportTime', 'reportTo') as
          | Date
          | string
          | undefined;
        return reportTo != null && new Date(reportTo) < TC_RPM_LEGACY_MODEL_CUTOFF;
      };

      // no vessel/rpm/tcRpm to calculate from: clear the field directly rather than requesting a calculation
      const buildTcRpmPowerAction = (
        tcRpm1: number | undefined,
        tcRpm2: number | undefined,
        rpm: number | undefined
      ) => {
        const vesselId = appStore.selectedVesselId();
        if (!vesselId || rpm == null || (tcRpm1 == null && tcRpm2 == null)) {
          return powerCalculationEvents.clearTcRpmPower();
        }

        return powerCalculationEvents.getTcRpmPower({ tcrpm1: tcRpm1, tcrpm2: tcRpm2, rpm });
      };

      // Fields only shown while shore power is in use. Clearing `endTime` here is enough to
      // hide/reset the end-time-dependent fields too — that clear cascades through
      // clearShorePowerEndTimeDependentFieldsOnClear$ below, so it isn't repeated in this list.
      const SHORE_POWER_IN_USE_DEPENDENT_FIELDS: { fieldId: ShorePowerFormFieldId; value: StateFieldType }[] = [
        { fieldId: 'receptionPort', value: undefined },
        { fieldId: 'startTime', value: undefined },
        { fieldId: 'endTime', value: undefined },
        { fieldId: 'kwh', value: undefined },
      ];

      // Fields only shown once shore power's end time has been set (see shore-power.component.html) —
      // hiding them doesn't clear their stored values on its own, so this list drives an explicit reset.
      const SHORE_POWER_END_TIME_DEPENDENT_FIELDS: { fieldId: ShorePowerFormFieldId; value: StateFieldType }[] = [
        { fieldId: 'totalKwh', value: undefined },
        { fieldId: 'wtt', value: undefined },
        { fieldId: 'ednNumber', value: undefined },
        { fieldId: 'ednFiles', value: [] },
        { fieldId: 'sustainableIsUsed', value: undefined },
        { fieldId: 'proofOfSustainabilityEdnFiles', value: [] },
      ];

      return {
        recalculateTcRpmPowerOnValueChange$: events.on(powerEvents.updateFormStateValue).pipe(
          filter(
            ({ payload }) => payload.sectionId === 'mainEngine' && TC_RPM_TRIGGER_FIELDS.includes(payload.fieldId)
          ),
          map(({ payload }) => {
            const tcRpm1 =
              payload.fieldId === 'tcRpm1' ? (payload.value as number | undefined) : getCurrentValue('tcRpm1');
            const tcRpm2 =
              payload.fieldId === 'tcRpm2' ? (payload.value as number | undefined) : getCurrentValue('tcRpm2');
            const rpm = payload.fieldId === 'rpm' ? (payload.value as number | undefined) : getCurrentValue('rpm');

            return buildTcRpmPowerAction(tcRpm1, tcRpm2, rpm);
          })
        ),
        recalculateTcRpmPowerOnInit$: events.on(powerEvents.initialiseFormState).pipe(
          map(({ payload: { initialData } }) => {
            const mainEngine = (initialData as { mainEngine?: Record<string, unknown> } | undefined)?.mainEngine;
            if (!mainEngine) return powerCalculationEvents.clearTcRpmPower();

            return buildTcRpmPowerAction(
              mainEngine['tcRpm1'] as number | undefined,
              mainEngine['tcRpm2'] as number | undefined,
              mainEngine['rpm'] as number | undefined
            );
          })
        ),
        getTcRpmPower$: events.on(powerCalculationEvents.getTcRpmPower).pipe(
          switchMap(({ payload: { tcrpm1, tcrpm2, rpm } }) => {
            const vesselId = appStore.selectedVesselId();
            if (!vesselId) return EMPTY;

            const engineModes = getEngineModes();
            const legacyReport = isLegacyReport();

            return reportService.getTcRpmPower(vesselId, tcrpm1, tcrpm2, engineModes).pipe(
              switchMap(({ tcrpmPower }) => {
                if (legacyReport) return of(tcrpmPower);

                return reportService.getMeRpmPower(vesselId, rpm, engineModes).pipe(
                  map(({ meRPMPower }) => {
                    if (tcrpmPower != null && meRPMPower != null) {
                      return (tcrpmPower + meRPMPower) / 2;
                    }
                    return tcrpmPower ?? meRPMPower;
                  })
                );
              }),
              mapResponse({
                next: (tcrpmPower) => powerCalculationEvents.getTcRpmPowerSuccess({ tcrpmPower }),
                error: (err: Error) => {
                  console.error('getTcRpmPower error:', err);
                  return powerCalculationEvents.getTcRpmPowerFailed(err);
                },
              }),
              // A clear makes the answer of this call out of date.
              takeUntil(events.on(powerCalculationEvents.clearTcRpmPower))
            );
          })
        ),
        reconcileFlowMetersOnSpec$: events.on(vesselSpecEvents.fetchVesselSpecSuccess).pipe(
          map(({ payload }) =>
            powerFlowMeterEvents.initialiseFlowMeterInstallation({
              paths: collectFlowMeterInstallationPaths(payload),
            })
          )
        ),
        reconcileFlowMetersOnInit$: events.on(powerEvents.initialiseFormState).pipe(
          map(() => vesselSpecStore.vesselSpec()),
          filter((vesselSpec): vesselSpec is VesselSpec => vesselSpec != null),
          map((vesselSpec) =>
            powerFlowMeterEvents.initialiseFlowMeterInstallation({
              paths: collectFlowMeterInstallationPaths(vesselSpec),
            })
          )
        ),
        // mergeMap, not switchMap: on Save, flushPendingEdnFilesOnSave$ below can dispatch both
        // the edn and ednPos uploads back-to-back — switchMap would cancel the first upload's
        // HTTP request as soon as the second one is dispatched.
        uploadEdnFile$: events.on(shorePowerDocumentEvents.uploadEdnFile).pipe(
          mergeMap(({ payload: { vesselId, reportId, ednType, file } }) =>
            reportService.uploadEdnFile(vesselId, reportId, ednType, file).pipe(
              map((files) => shorePowerDocumentEvents.uploadEdnFileSuccess({ ednType, files })),
              catchError((err) =>
                of(
                  shorePowerDocumentEvents.uploadEdnFileFailed({
                    ednType,
                    error: err instanceof Error ? err.message : 'Unknown error',
                  })
                )
              )
            )
          )
        ),
        applyUploadEdnFileOnSuccess$: events
          .on(shorePowerDocumentEvents.uploadEdnFileSuccess)
          .pipe(
            map(({ payload: { ednType, files } }) =>
              powerEvents.updateFormStateValue({ sectionId: 'shorePower', fieldId: ednFieldId(ednType), value: files })
            )
          ),
        notifyUploadEdnFileSuccess$: events
          .on(shorePowerDocumentEvents.uploadEdnFileSuccess)
          .pipe(
            tap(() =>
              toastDispatcher.toastSuccess({ summary: 'File Uploaded', detail: 'Your file was uploaded successfully.' })
            )
          ),
        notifyUploadEdnFileFailed$: events.on(shorePowerDocumentEvents.uploadEdnFileFailed).pipe(
          tap(({ payload: { error } }) =>
            toastDispatcher.toastError({
              summary: 'Upload Failed',
              detail: `Your file could not be uploaded: ${error}`,
            })
          )
        ),
        deleteEdnFile$: events.on(shorePowerDocumentEvents.deleteEdnFile).pipe(
          switchMap(({ payload: { vesselId, reportId, ednType, dropboxUrl } }) =>
            reportService.deleteEdnFile(vesselId, reportId, ednType, dropboxUrl).pipe(
              map((files) => shorePowerDocumentEvents.deleteEdnFileSuccess({ ednType, files })),
              catchError((err) =>
                of(
                  shorePowerDocumentEvents.deleteEdnFileFailed({
                    ednType,
                    error: err instanceof Error ? err.message : 'Unknown error',
                  })
                )
              )
            )
          )
        ),
        applyDeleteEdnFileOnSuccess$: events
          .on(shorePowerDocumentEvents.deleteEdnFileSuccess)
          .pipe(
            map(({ payload: { ednType, files } }) =>
              powerEvents.updateFormStateValue({ sectionId: 'shorePower', fieldId: ednFieldId(ednType), value: files })
            )
          ),
        notifyDeleteEdnFileSuccess$: events
          .on(shorePowerDocumentEvents.deleteEdnFileSuccess)
          .pipe(
            tap(() =>
              toastDispatcher.toastSuccess({ summary: 'File Deleted', detail: 'Your file was deleted successfully.' })
            )
          ),
        notifyDeleteEdnFileFailed$: events.on(shorePowerDocumentEvents.deleteEdnFileFailed).pipe(
          tap(({ payload: { error } }) =>
            toastDispatcher.toastError({
              summary: 'Delete Failed',
              detail: `Your file could not be deleted: ${error}`,
            })
          )
        ),
        clearShorePowerFieldsOnUnuse$: events.on(powerEvents.updateFormStateValue).pipe(
          filter(({ payload }) => payload.sectionId === 'shorePower' && payload.fieldId === 'isUsed' && !payload.value),
          tap(() => clearShorePowerFields(SHORE_POWER_IN_USE_DEPENDENT_FIELDS))
        ),
        clearShorePowerEndTimeDependentFieldsOnClear$: events.on(powerEvents.updateFormStateValue).pipe(
          filter(
            ({ payload }) => payload.sectionId === 'shorePower' && payload.fieldId === 'endTime' && !payload.value
          ),
          tap(() => clearShorePowerFields(SHORE_POWER_END_TIME_DEPENDENT_FIELDS))
        ),
        // Reception port always tracks the operational port name live, the same way the
        // legacy report kept them in lockstep with a $watch on report.operational.port_name.
        syncShorePowerReceptionPortFromPortName$: events.on(operationalEvents.updateFormStateValue).pipe(
          filter(
            ({ payload }) =>
              payload.sectionId === 'portAndPosition' && payload.fieldId === 'portName' && !!payload.value
          ),
          tap(({ payload }) =>
            powerDispatcher.updateFormStateValue({
              sectionId: 'shorePower',
              fieldId: 'receptionPort',
              value: payload.value,
            })
          )
        ),
        // Catches up reception port with whatever port name is already set the moment shore
        // power is switched on — the live sync above only reacts to port name changes *after*
        // it's subscribed, so a port name set earlier in the session wouldn't otherwise apply.
        syncShorePowerReceptionPortOnUse$: events.on(powerEvents.updateFormStateValue).pipe(
          filter(
            ({ payload }) => payload.sectionId === 'shorePower' && payload.fieldId === 'isUsed' && !!payload.value
          ),
          tap(() => syncShorePowerReceptionPortFromCurrentPortName())
        ),
        flushPendingEdnFilesOnSave$: events.on(reportEvents.saveReportSuccess).pipe(
          tap(({ payload: { reportId } }) => {
            const vesselId = appStore.selectedVesselId();
            if (!vesselId) return;

            (Object.entries(store.pendingEdnFiles()) as [EdnFileType, File][]).forEach(([ednType, file]) => {
              shorePowerDocumentDispatcher.uploadEdnFile({ vesselId, reportId, ednType, file });
              shorePowerDocumentDispatcher.dequeueEdnFile({ ednType });
            });
          })
        ),
      };
    })
  );
}
